import { Link } from "react-router-dom";
import { ArrowLeft, FileText } from "lucide-react";
import { useSEO } from "@/lib/seo";

/**
 * Terms of Service — DRAFT placeholder pending counsel review.
 *
 * This page exists so that paying users have a Terms link to point at while
 * we finalise the formal Terms of Service with an attorney. The wording is
 * intentionally conservative, broad, and includes a prominent "draft" banner
 * so we do not lock ourselves into commitments we have not had counsel review.
 *
 * Do NOT remove the "Draft" banner without legal sign-off.
 */
export default function Terms() {
  useSEO({
    title: "Terms of use",
    description: "Draft terms of use for SonSpeak — pending counsel review.",
    path: "/terms",
  });

  return (
    <div className="min-h-screen px-4 sm:px-8 py-8" data-testid="terms-page">
      <div className="max-w-3xl mx-auto">
        <header className="flex items-center gap-3 mb-6">
          <Link to="/" className="pill-btn" data-testid="terms-back">
            <ArrowLeft size={16} /> Back
          </Link>
          <h1 className="display text-2xl sm:text-3xl font-extrabold flex items-center gap-2">
            <FileText size={26} className="text-[color:var(--coral)]" />
            Terms of use
          </h1>
        </header>

        <div className="brutal-card bg-[#FFE9C7] border-[3px] border-[color:var(--ink)] p-4 mb-4 text-sm leading-relaxed" data-testid="terms-draft-banner">
          <strong>⚠️ Draft — pending counsel review.</strong> This page describes the
          current understanding between SonSpeak and the people who use it.
          It is a working draft and will be replaced with a formal Terms of
          Service after attorney review. By continuing to use SonSpeak you
          agree to the practices described below, which are subject to change
          as we finalise the formal terms. Questions: <a className="underline font-semibold" href="mailto:hello@sonspeak.com">hello@sonspeak.com</a>.
        </div>

        <div className="brutal-card bg-white p-6 prose-like space-y-5">
          <p className="text-[color:var(--ink-soft)]">Last updated May 2026.</p>

          <section>
            <h2 className="display text-xl font-bold mb-2">1. What SonSpeak is</h2>
            <p>
              SonSpeak is an affordable communication-support application —
              an AAC board with text-to-speech, custom photos, and custom
              voice recording. It is offered through the website at
              sonspeak.com and as a Progressive Web App. SonSpeak is{" "}
              <strong>not</strong> a medical device, a clinical tool, a
              substitute for speech-language therapy, or a substitute for
              any other professional service. SonSpeak does not diagnose,
              treat, or make claims about developmental outcomes.
            </p>
          </section>

          <section>
            <h2 className="display text-xl font-bold mb-2">2. Who can sign up</h2>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>You must be 13 or older to create an account.</li>
              <li>
                If you are creating an account on behalf of a child under 13
                (the typical case), you must be the child's parent or legal
                guardian, or you must have written authorisation from one.
              </li>
              <li>
                SonSpeak is currently offered in the United States. If you
                are outside the U.S., you can join the waitlist; we will
                contact you when we open in your country.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="display text-xl font-bold mb-2">3. Subscriptions and refunds</h2>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>
                Paid plans are billed by Stripe. The current plans are
                Starter — Graduated Boards at $12/year, and Membership at
                $7/month or $70/year. There is no trial period; your plan
                renews automatically at the price you selected (annual or
                monthly) until you cancel.
              </li>
              <li>
                You can cancel at any time from your account settings.
                Cancellation stops future renewals. The current paid period
                continues until its scheduled end date.
              </li>
              <li>
                If a plan lapses, you keep access for a 14-day grace window.
                Your boards are never deleted, and you can export your
                content at any time regardless of subscription status.
              </li>
              <li>
                If something is wrong with a charge, email{" "}
                <a className="underline" href="mailto:hello@sonspeak.com">hello@sonspeak.com</a>{" "}
                within 14 days and we will make it right. We are a small
                team; we want you to be happy with the service.
              </li>
              <li>
                "Supported access" is granted case-by-case for qualifying
                families and can be requested by email. It is not a general
                unpaid tier.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="display text-xl font-bold mb-2">4. Your content</h2>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>
                The tiles, photos, voice recordings, and other content you
                save in SonSpeak belong to you.
              </li>
              <li>
                We do not sell your content, do not share it with
                advertisers, and do not use it to train AI models.
              </li>
              <li>
                You can export your content as a single file from{" "}
                <strong>Settings → Backup &amp; restore</strong>, or
                permanently delete it from{" "}
                <strong>Settings → Danger zone</strong>.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="display text-xl font-bold mb-2">5. Acceptable use</h2>
            <p>You agree not to use SonSpeak to:</p>
            <ul className="list-disc pl-5 space-y-1.5 mt-2">
              <li>Upload content you do not have the right to use.</li>
              <li>Upload sexually explicit content, harassment, threats, or
                content depicting minors in unsafe contexts.</li>
              <li>Resell, white-label, scrape, or otherwise abuse the service.</li>
              <li>Attempt to access another user's account or board.</li>
            </ul>
            <p className="mt-3">
              We may suspend or close accounts that violate these rules.
              Where possible, we will give notice and a chance to export
              your data first.
            </p>
          </section>

          <section>
            <h2 className="display text-xl font-bold mb-2">6. What SonSpeak does not promise</h2>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>SonSpeak is not a clinical or medical tool and does not
                replace any professional service.</li>
              <li>SonSpeak makes no claim about developmental outcomes,
                language gain, or therapeutic effect.</li>
              <li>SonSpeak is provided "as is" while we are in active
                development. Features can change. Outages can happen.
                Internet connectivity, third-party platforms (Apple, Google,
                Stripe, Resend, your device manufacturer), and the practical
                realities of running a small software company may sometimes
                affect availability.</li>
            </ul>
          </section>

          <section>
            <h2 className="display text-xl font-bold mb-2">7. Limitation of liability</h2>
            <p>
              To the maximum extent allowed by law, SonSpeak's total
              liability to you for any claim arising out of your use of the
              service is limited to the amount you paid us in the twelve
              months before the claim. Some jurisdictions do not allow this
              kind of limit, in which case the limit is the lowest amount
              the law allows. Nothing in these terms excludes liability for
              fraud, gross negligence, or anything else that the law does
              not allow us to exclude.
            </p>
          </section>

          <section>
            <h2 className="display text-xl font-bold mb-2">8. Changes to these terms</h2>
            <p>
              We will replace this draft with a counsel-reviewed Terms of
              Service when our attorney finishes review. Until then, this
              page may be updated; the "last updated" date at the top will
              change. Material changes (e.g. price, refund policy, governing
              law) will be communicated by email to active subscribers.
            </p>
          </section>

          <section>
            <h2 className="display text-xl font-bold mb-2">9. Contact</h2>
            <p>
              Email{" "}
              <a className="underline font-semibold" href="mailto:hello@sonspeak.com">
                hello@sonspeak.com
              </a>{" "}
              for any question, request, or concern. We read every email.
            </p>
          </section>
        </div>

        <p className="text-center text-xs text-[color:var(--ink-soft)] mt-6">
          SonSpeak · Draft terms pending counsel review · No ads · No tracking
        </p>
      </div>
    </div>
  );
}
